import React, { Fragment, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import { logger } from 'common/utils/helpers';
import { useGetDiagnosticPackageActionQuery, useRetrieveDiagnosticPackageMutation } from 'apis/dl';
import Button from 'components/shared/Button';
import { DiagnosticPackageStatusNames, DiagnosticPackageStatuses } from './constants';
import DiagnosticPackageStepper from './DiagnosticPackageStepper';
import styles from './DiagnosticPackageStatusBar.module.css';

function DiagnosticPackageStatusBar({ diagPkgId, onComplete, onRetry }) {
  const [failed, setFailed] = useState(false);
  const { data } = useGetDiagnosticPackageActionQuery({ diagPkgId }, { enabled: !failed, refetchInterval: 15 * 1000 });
  const { mutateAsync, isLoading } = useRetrieveDiagnosticPackageMutation();
  const handleRetry = useCallback(
    async ({ deviceId }) => {
      try {
        const result = await mutateAsync({ deviceId });
        onRetry(result.diagPkgId);
      } catch (error) {
        logger.error(error);
      }
    },
    [mutateAsync, onRetry]
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    if (data.runtimeStatus === 'Completed') {
      if (data.customStatus.status === DiagnosticPackageStatuses.COMPLETED) {
        onComplete(data.commissioningId);
      } else {
        setFailed(true);
      }
    } else if (data.runtimeStatus === 'Failed') {
      setFailed(true);
    }
  }, [onComplete, data]);

  const statusName = DiagnosticPackageStatusNames[data?.customStatus?.status] ?? 'Retrieving diagnostic package';

  return (
    <Fragment>
      <div className={classNames(styles.status, { [styles.failed]: failed })}>{statusName}</div>
      {failed ? (
        <div className={styles.actions}>
          <Button
            variant="mixed"
            color="primary"
            size="x-small"
            disabled={isLoading}
            onClick={() => handleRetry(data.input)}
          >
            Try again
          </Button>
        </div>
      ) : (
        <div className={styles.progress}>
          <DiagnosticPackageStepper status={data?.customStatus?.status} />
        </div>
      )}
    </Fragment>
  );
}

export default DiagnosticPackageStatusBar;
